<script setup>
import { ref, computed, nextTick, watch, onUnmounted } from 'vue';
import { useFlowieHandoff } from '@/composables/useFlowieHandoff'

const props = defineProps({
    messages: { type: Array, default: () => [] },
    isGenerating: { type: Boolean, default: false },
    mode: { type: String, required: true },
    specialist: { type: String, required: true },
    settings: { type: Object, required: true },
    availableTags: { type: Array, default: () => [] },
    canUndoRewrite: { type: Boolean, default: false },
    initialMessage: { type: String, default: '' },
});

const emit = defineEmits(['send', 'accept-setting', 'undo-rewrite']);

const messageInput = ref('');

const { flowieComposerVisible, closeFlowieComposer } = useFlowieHandoff(
    () => props.initialMessage,
    messageInput
);
const messagesContainer = ref(null);
const funMessageIndex = ref(0);
const funMessageInterval = ref(null);
const progressWidth = ref(0);
const progressInterval = ref(null);

const funMessages = [
    { icon: 'M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75', text: _mft('email-ai:fun.craftingSubject') },
    { icon: 'M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z', text: _mft('email-ai:fun.writingCopy') },
    { icon: 'M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z', text: _mft('email-ai:fun.polishingContent') },
    { icon: 'M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z', text: _mft('email-ai:fun.optimizing') },
    { icon: 'M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z M15 12a3 3 0 11-6 0 3 3 0 016 0z', text: _mft('email-ai:fun.reviewingDetails') },
    { icon: 'M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.58-5.84a14.927 14.927 0 00-2.58 5.84', text: _mft('email-ai:fun.almostThere') },
];

const currentFunMessage = computed(() => funMessages[funMessageIndex.value % funMessages.length]);

const startFunMessages = () => {
    funMessageIndex.value = Math.floor(Math.random() * funMessages.length);
    progressWidth.value = 0;

    funMessageInterval.value = setInterval(() => {
        funMessageIndex.value = (funMessageIndex.value + 1) % funMessages.length;
    }, 3500);

    let elapsed = 0;
    progressInterval.value = setInterval(() => {
        elapsed += 200;
        progressWidth.value = Math.min(90, 30 * Math.log10(elapsed / 500 + 1));
    }, 200);
};

const stopFunMessages = () => {
    if (funMessageInterval.value) {
        clearInterval(funMessageInterval.value);
        funMessageInterval.value = null;
    }
    if (progressInterval.value) {
        clearInterval(progressInterval.value);
        progressInterval.value = null;
    }
    progressWidth.value = 100;
};

const formatSuggestedSchedule = (schedule) => {
    if (schedule === 'draft') {
        return props.mode === 'automation'
            ? _mft('communication:automation.schedule.inactive.title')
            : _mft('email-ai:settings.saveWithoutSending');
    }

    if (schedule === 'now' || schedule === 'send_immediately') {
        return props.mode === 'automation'
            ? _mft('communication:automation.schedule.active.title')
            : _mft('email-ai:settings.sendNow');
    }

    if (schedule === 'schedule') {
        return _mft('communication:automation.saveAndSchedule');
    }

    return schedule;
};

watch(() => props.isGenerating, (val) => {
    if (val) startFunMessages();
    else stopFunMessages();
});

const renderMarkdown = (text) => {
    if (!text) return '';
    const normalizedText = text.replace(/\\n/g, '\n');
    let html = normalizedText
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    // Headings
    html = html.replace(/^#### (.+)$/gm, '<h4 class="font-semibold text-sm text-gray-700 mt-3 mb-1">$1</h4>');
    html = html.replace(/^### (.+)$/gm, '<h3 class="font-semibold text-sm text-gray-800 mt-3 mb-1">$1</h3>');
    html = html.replace(/^## (.+)$/gm, '<h2 class="font-bold text-base text-gray-900 mt-2 mb-1">$1</h2>');

    // Bold & italic
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');

    // Tables
    html = html.replace(/^\|(.+)\|\s*\n\|[-| :]+\|\s*\n((?:\|.+\|\s*\n?)*)/gm, (match, headerRow, bodyRows) => {
        const headers = headerRow.split('|').map(h => h.trim()).filter(Boolean);
        const headerHtml = headers.map(h => `<th class="px-2 py-1 text-left text-xs font-semibold text-gray-600 border-b border-gray-300">${h}</th>`).join('');
        const rows = bodyRows.trim().split('\n').map(row => {
            const cells = row.replace(/^\||\|$/g, '').split('|').map(c => c.trim());
            return '<tr>' + cells.map(c => `<td class="px-2 py-1 text-xs text-gray-700 border-b border-gray-100">${c}</td>`).join('') + '</tr>';
        }).join('');
        return `<table class="w-full my-2 border-collapse"><thead><tr>${headerHtml}</tr></thead><tbody>${rows}</tbody></table>`;
    });

    // Lists
    html = html.replace(/^(\d+)\.\s+(.+)$/gm, '<li class="ml-4 list-decimal">$2</li>');
    html = html.replace(/^[-•]\s+(.+)$/gm, '<li class="ml-4 list-disc">$1</li>');

    html = html.replace(/((?:<li class="ml-4 list-disc">.+<\/li>\s*)+)/g, '<ul class="my-1 space-y-0.5">$1</ul>');
    html = html.replace(/((?:<li class="ml-4 list-decimal">.+<\/li>\s*)+)/g, '<ol class="my-1 space-y-0.5">$1</ol>');

    // Paragraphs
    html = html.replace(/\n\n/g, '</p><p class="mt-2">');
    html = html.replace(/\n/g, '<br>');

    html = '<p>' + html + '</p>';
    html = html.replace(/<p>\s*(<h[2-4])/g, '$1');
    html = html.replace(/(<\/h[2-4]>)\s*<\/p>/g, '$1');
    html = html.replace(/<p>\s*(<table)/g, '$1');
    html = html.replace(/(<\/table>)\s*<\/p>/g, '$1');
    html = html.replace(/<p>\s*(<ul|<ol)/g, '$1');
    html = html.replace(/(<\/ul>|<\/ol>)\s*<\/p>/g, '$1');
    html = html.replace(/<p>\s*<\/p>/g, '');

    return html;
};

const sendMessage = () => {
    const text = messageInput.value.trim();
    if (!text || props.isGenerating) return;
    messageInput.value = '';
    emit('send', text);
};

const handleKeydown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
    }
};

const acceptSettingClick = (key, value) => {
    emit('accept-setting', { key, value });
};

const normalizedStringArray = (values) => [...values].map(String).sort();

const arraysMatch = (left, right) => {
    if (left.length !== right.length) {
        return false;
    }

    return left.every((value, index) => value === right[index]);
};

const isSettingApplied = (key, value) => {
    if (key === 'schedule') {
        return props.settings.schedule === value;
    }

    if (key === 'delay_hours') {
        return props.settings.delayHours === value;
    }

    if (key === 'trigger_tag_name') {
        return props.settings.triggerTag?.name === value;
    }

    if (key === 'target_tags') {
        return arraysMatch(
            normalizedStringArray(props.settings.targetTags.map(tag => tag.name)),
            normalizedStringArray(Array.isArray(value) ? value : []),
        );
    }

    if (key === 'exclude_tags') {
        return arraysMatch(
            normalizedStringArray(props.settings.excludeTags.map(tag => tag.name)),
            normalizedStringArray(Array.isArray(value) ? value : []),
        );
    }

    return false;
};

watch(() => props.messages.length, () => {
    nextTick(() => {
        if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight;
        }
    });
});

onUnmounted(() => {
    stopFunMessages();
});
</script>

<template>
    <div class="flex-1 flex flex-col overflow-hidden">
        <flowie-handoff-composer
            v-model="messageInput"
            :visible="flowieComposerVisible"
            :disabled="isGenerating"
            :placeholder="_mft('email-ai:placeholder')"
            @close="closeFlowieComposer"
            @send="sendMessage"
        />

        <!-- Messages -->
        <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-3">
            <div v-if="messages.length === 0 && !isGenerating" class="text-center text-gray-400 text-sm mt-8">
                <div class="flex justify-center pb-4">
                    <ai-agent-newsletter-specialist-avatar v-if="specialist === 'alex'" class="w-12 h-12" />
                    <ai-agent-automation-expert-avatar v-if="specialist === 'leo'" class="w-12 h-12" />
                </div>

                <p class="mb-2">
                    <mf-translate :domainkey="'email-ai:' + specialist + '.emptyState'"></mf-translate>
                </p>
                <p class="text-xs">
                    <mf-translate :domainkey="'email-ai:' + specialist + '.emptyStateExample'"></mf-translate>
                </p>
            </div>

            <div
                v-for="(msg, index) in messages"
                :key="index"
                :class="msg.role === 'user' ? 'flex justify-end' : 'flex justify-start'"
            >
                <div class="max-w-[85%]">
                    <div
                        v-if="msg.role === 'user'"
                        class="rounded-2xl rounded-br-md px-4 py-2 text-sm whitespace-pre-line"
                        :class="mode === 'newsletter' ? 'bg-aiAgentNewsletterSpecialist text-gray-900' : 'bg-aiAgentAutomationExpert text-gray-900'"
                    >
                        {{ msg.content }}
                    </div>
                    <div
                        v-else
                        class="bg-gray-100 text-gray-800 rounded-2xl rounded-bl-md px-4 py-2 text-sm"
                        v-html="renderMarkdown(msg.content)"
                    ></div>

                    <utility-ai-usage-cta v-if="msg.budgetExceeded" class="mt-2 pl-1" />

                    <!-- Inline action buttons for suggested settings -->
                    <div v-if="msg.role === 'assistant' && msg.suggested_settings" class="mt-2 space-y-1.5 pl-2">
                        <!-- Target tags -->
                        <div v-if="msg.suggested_settings.target_tags?.length" class="flex items-center gap-2 text-xs flex-wrap">
                            <span class="text-gray-500">{{ _mft('email-ai:settings.sendTo') }}:</span>
                            <span v-for="tag in msg.suggested_settings.target_tags" :key="tag" class="px-2 py-0.5 bg-gray-100 rounded font-medium">{{ tag }}</span>
                            <button-secondary
                                v-if="!isSettingApplied('target_tags', msg.suggested_settings.target_tags)"
                                @click.stop="acceptSettingClick('target_tags', msg.suggested_settings.target_tags)"
                                size="xs"
                            >{{ _mft('email-ai:accept') }}</button-secondary>
                            <span v-else class="px-2 py-0.5 text-green-700 bg-green-100 rounded">&#10003;</span>
                        </div>

                        <!-- Schedule -->
                        <div v-if="msg.suggested_settings.send_schedule" class="flex items-center gap-2 text-xs">
                            <span class="text-gray-500">{{ _mft('email-ai:settings.schedule') }}:</span>
                            <span class="px-2 py-0.5 bg-gray-100 rounded font-medium">
                                {{ formatSuggestedSchedule(msg.suggested_settings.send_schedule) }}
                            </span>
                            <button-secondary
                                v-if="!isSettingApplied('schedule', msg.suggested_settings.send_schedule)"
                                @click.stop="acceptSettingClick('schedule', msg.suggested_settings.send_schedule)"
                                size="xs"
                            >{{ _mft('email-ai:accept') }}</button-secondary>
                            <span v-else class="px-2 py-0.5 text-green-700 bg-green-100 rounded">&#10003;</span>
                        </div>

                        <!-- Trigger tag (automation) -->
                        <div v-if="msg.suggested_settings.trigger_tag_name" class="flex items-center gap-2 text-xs">
                            <span class="text-gray-500">{{ _mft('email-ai:settings.trigger') }}:</span>
                            <span class="px-2 py-0.5 bg-gray-100 rounded font-medium">{{ msg.suggested_settings.trigger_tag_name }}</span>
                            <button-secondary
                                v-if="!isSettingApplied('trigger_tag_name', msg.suggested_settings.trigger_tag_name)"
                                @click.stop="acceptSettingClick('trigger_tag_name', msg.suggested_settings.trigger_tag_name)"
                                size="xs"
                            >{{ _mft('email-ai:accept') }}</button-secondary>
                            <span v-else class="px-2 py-0.5 text-green-700 bg-green-100 rounded">&#10003;</span>
                        </div>

                        <!-- Delay (automation) -->
                        <div v-if="msg.suggested_settings.delay_hours !== undefined" class="flex items-center gap-2 text-xs">
                            <span class="text-gray-500">{{ _mft('email-ai:settings.delay') }}:</span>
                            <span class="px-2 py-0.5 bg-gray-100 rounded font-medium">{{ msg.suggested_settings.delay_hours }}h</span>
                            <button-secondary
                                v-if="!isSettingApplied('delay_hours', msg.suggested_settings.delay_hours)"
                                @click.stop="acceptSettingClick('delay_hours', msg.suggested_settings.delay_hours)"
                                size="xs"
                            >{{ _mft('email-ai:accept') }}</button-secondary>
                            <span v-else class="px-2 py-0.5 text-green-700 bg-green-100 rounded">&#10003;</span>
                        </div>

                        <!-- Exclude tags -->
                        <div v-if="msg.suggested_settings.exclude_tags?.length" class="flex items-center gap-2 text-xs flex-wrap">
                            <span class="text-gray-500">{{ _mft('email-ai:settings.exclude') }}:</span>
                            <span v-for="tag in msg.suggested_settings.exclude_tags" :key="tag" class="px-2 py-0.5 bg-gray-100 rounded font-medium">{{ tag }}</span>
                            <button-secondary
                                v-if="!isSettingApplied('exclude_tags', msg.suggested_settings.exclude_tags)"
                                @click.stop="acceptSettingClick('exclude_tags', msg.suggested_settings.exclude_tags)"
                                size="xs"
                            >{{ _mft('email-ai:accept') }}</button-secondary>
                            <span v-else class="px-2 py-0.5 text-green-700 bg-green-100 rounded">&#10003;</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Generating indicator -->
            <div v-if="isGenerating" class="flex justify-start generating-appear">
                <div class="w-full max-w-[90%] rounded-2xl rounded-bl-md overflow-hidden shadow-lg border border-gray-100 bg-gradient-to-br from-gray-50 via-white to-gray-50">
                    <div class="h-1 w-full bg-gray-200 overflow-hidden">
                        <div
                            class="h-full transition-all duration-700 ease-out"
                            :class="mode === 'newsletter' ? 'bg-aiAgentNewsletterSpecialist' : 'bg-aiAgentAutomationExpert'"
                            :style="{ width: progressWidth + '%' }"
                        ></div>
                    </div>
                    <div class="px-5 py-4">
                        <div class="flex items-center gap-3 mb-3">
                            <div
                                class="generating-icon flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center"
                                :class="mode === 'newsletter' ? 'bg-aiAgentNewsletterSpecialist' : 'bg-aiAgentAutomationExpert'"
                                :key="funMessageIndex"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                    <path stroke-linecap="round" stroke-linejoin="round" :d="currentFunMessage.icon" />
                                </svg>
                            </div>
                            <span class="text-sm font-medium text-gray-700 generating-text" :key="'t' + funMessageIndex">{{ currentFunMessage.text }}</span>
                        </div>
                        <div class="space-y-2">
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer" style="width: 65%"></div>
                                <div class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer" style="width: 25%; animation-delay: 0.3s"></div>
                            </div>
                            <div class="flex gap-2">
                                <div class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer" style="width: 40%; animation-delay: 0.15s"></div>
                                <div class="h-2 rounded-full bg-gray-200/60 skeleton-shimmer" style="width: 50%; animation-delay: 0.45s"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="canUndoRewrite" class="flex justify-start">
                <button-secondary
                    @click="emit('undo-rewrite')"
                    size="sm"
                >
                    {{ _mft('shared:undo') }}
                </button-secondary>
            </div>
        </div>

        <!-- Input area -->
        <div class="border-t border-gray-200 p-3">
            <div class="flex items-end space-x-2">
                <textarea
                    v-model="messageInput"
                    @keydown="handleKeydown"
                    :disabled="isGenerating"
                    :placeholder="_mft('email-ai:placeholder')"
                    rows="2"
                    class="flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 disabled:opacity-50"
                    autofocus
                ></textarea>
                <button-base
                    @click="sendMessage"
                    style-type="primary"
                    :disabled="!messageInput.trim() || isGenerating"
                    size="sm"
                    class="flex-shrink-0 !px-2 !py-2"
                >
                    <template #icon>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z"/>
                        </svg>
                    </template>
                </button-base>
            </div>
        </div>
    </div>
</template>

<style scoped>
@keyframes skeleton-shimmer {
    0%, 100% { opacity: 0.4; }
    50% { opacity: 1; }
}
.skeleton-shimmer {
    animation: skeleton-shimmer 2s ease-in-out infinite;
}

@keyframes generating-appear {
    from { opacity: 0; transform: translateY(8px) scale(0.97); }
    to { opacity: 1; transform: translateY(0) scale(1); }
}
.generating-appear {
    animation: generating-appear 0.4s ease-out;
}

@keyframes icon-pop {
    0% { transform: scale(0) rotate(-20deg); }
    60% { transform: scale(1.15) rotate(5deg); }
    100% { transform: scale(1) rotate(0deg); }
}
.generating-icon {
    animation: icon-pop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes text-slide {
    from { opacity: 0; transform: translateX(-8px); }
    to { opacity: 1; transform: translateX(0); }
}
.generating-text {
    animation: text-slide 0.4s ease-out;
}
</style>
